import React from 'react'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Paper from '@mui/material/Paper'
import { useTranslation } from 'react-i18next'
import { VirtualizedListbox, VirtualizedPopper } from '@components/VirtualizedListbox/VirtualizedListbox'
import { OutOfStockStatusEnum } from '../../../../generated/gqlTypes'
import { ProductVariantPropertyJson } from '../types'

const INITIAL_VAL = '--- Click to select ---'

interface Props {
  options: string[]
  label: string
  selected: string
  currentProps: ProductVariantPropertyJson[][]
  allProps: ProductVariantPropertyJson[][]

  onChange(label: string, option: string): void
}

const VariantPickerWidget: React.FC<Props> = ({ options, label, selected, onChange, currentProps, allProps }) => {
  const { t } = useTranslation()

  const isInitial = selected === INITIAL_VAL || (!selected && options.length > 1)

  const initialSelected = options.length > 1 ? INITIAL_VAL : options[0]
  return (
    <div className='mb-20'>
      <label className='font-medium text-15 mb-8' htmlFor={label}>
        {label}
      </label>
      <Autocomplete
        fullWidth
        disableClearable
        autoComplete
        options={options.length > 1 ? [INITIAL_VAL, ...options] : options}
        value={selected || initialSelected}
        onChange={(e, value: string) => {
          onChange(label, value)
        }}
        getOptionLabel={option => {
          if (option === INITIAL_VAL) {
            return t('formOptions.clickToSelect', '--- Click to select ---')
          }
          if (currentProps.length) {
            const props = currentProps.find(props_ => props_.find(prop_ => prop_.valueName === option))

            if (props?.length) {
              const prop = props[0]
              let stockStatus = ''
              if (prop.stockStatus === OutOfStockStatusEnum.In) {
                stockStatus = ''
              } else if (prop.stockStatus === OutOfStockStatusEnum.Backorder) {
                stockStatus = t('productBackorder', 'Backorder')
              } else if (prop.stockStatus === OutOfStockStatusEnum.Out) {
                stockStatus = t('productOutOfStock', 'Out of Stock')
              }
              return stockStatus ? `${option} (${stockStatus})` : option
            }
          }

          if (allProps.length) {
            const props = allProps.filter(props_ => props_.find(prop_ => prop_.valueName === option))
            const isOutOfStock = props.flat().every(prop_ => prop_.stockStatus === OutOfStockStatusEnum.Out)
            if (isOutOfStock) {
              const stockStatus = t('productOutOfStock', 'Out of Stock')
              return `${option} (${stockStatus})`
            }
            const isBackorder = props
              .flat()
              .every(prop_ => prop_.stockStatus === OutOfStockStatusEnum.Backorder)
            if (isBackorder) {
              const stockStatus = t('productBackorder', 'Backorder')
              return `${option} (${stockStatus})`
            }
            return option
          }

          return option
        }}
        getOptionDisabled={option => {
          if (option === INITIAL_VAL) {
            return true
          }
          if (currentProps.length) {
            const props = currentProps.find(props_ => props_.find(prop_ => prop_.valueName === option))

            if (props?.length) {
              const prop = props[0]
              return prop.stockStatus === OutOfStockStatusEnum.Out
            }
          }

          if (allProps.length) {
            // all variants have the stock status "out"
            return allProps
              .filter(props_ => props_.find(prop_ => prop_.valueName === option))
              .flat()
              .every(prop_ => prop_.stockStatus === OutOfStockStatusEnum.Out)
          }

          return false
        }}
        disableListWrap
        PopperComponent={VirtualizedPopper}
        ListboxComponent={VirtualizedListbox}
        isOptionEqualToValue={(option, value) => option === value}
        PaperComponent={params => <Paper {...params} elevation={4} />}
        readOnly={options.length <= 1}
        forcePopupIcon={options.length > 1}
        renderInput={params => (
          <TextField
            {...params}
            name={label}
            variant='outlined'
            color='secondary'
            error={isInitial}
            margin='none'
            fullWidth
            inputProps={{
              ...params.inputProps,
              readOnly: options.length < 10,
              autoComplete: 'new-password',
              className: 'browser-default data-hj-allow ' + params.inputProps.className,
            }}
          />
        )}
      />
    </div>
  )
}
export default React.memo(VariantPickerWidget)
